import React, { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { FlagType } from '../../../../../../@types/product.enum';
import useContext from '../../../../../hooks/useContext';

interface IFlagsContext {
  flags: Record<FlagType, IFlag | undefined>;
  setFlags: (flags: IFlag[]) => void;
}

const FlagsContext = createContext<IFlagsContext | undefined>(undefined);

interface IFlagsProvider {
  children: ReactNode;
  flags: IFlag[];
}

const FlagsProvider = ({ children, flags: initialFlags }: IFlagsProvider) => {
  const [flags, setFlags] = useState<IFlag[]>(initialFlags);

  useEffect(() => {
    setFlags(initialFlags);
  }, [initialFlags]);
  const flagTypes = useMemo(
    () => ({
      [FlagType.main]: flags.find((f) => f.type === FlagType.main),
      [FlagType.news]: flags.find((f) => f.type === FlagType.news),
      [FlagType.price]: flags.find((f) => f.type === FlagType.price),
      [FlagType.arbitrary]: flags.find((f) => f.type === FlagType.arbitrary),
      [FlagType.delivery]: flags.find((f) => f.type === FlagType.delivery),
      [FlagType.outlet]: flags.find((f) => f.type === FlagType.outlet),
      [FlagType.buybuttoninactive]: flags.find((f) => f.type === FlagType.buybuttoninactive),
      [FlagType.featured]:
        flags.filter((i) => i.type === FlagType.featured).sort((a, b) => a.prio - b.prio)[0] ??
        null,
      [FlagType.favorite]:
        flags.filter((f) => f.type === FlagType.favorite).sort((a, b) => a.prio - b.prio)[0] ??
        null,
    }),
    [flags],
  );

  const context = useMemo(
    () => ({
      flags: flagTypes,
      setFlags,
    }),
    [flagTypes],
  );

  return <FlagsContext.Provider value={context}>{children}</FlagsContext.Provider>;
};

export default FlagsProvider;

export const useFlags = <T,>(
  type?: T,
): T extends FlagType ? IFlag | undefined : Record<FlagType, IFlag | undefined> => {
  const { flags } = useContext(FlagsContext);

  return (type ? flags[type as FlagType] : flags) as any;
};
